import React, { useState, useEffect, useCallback } from "react";
import {
    Layout, Table, Button, Form, Space, Row, Col, Breadcrumb, Modal,
    message, Typography, Select, DatePicker, Input, Tag, Descriptions, Dropdown,
    InputNumber, Divider, Tooltip, Alert, Spin, Popconfirm
} from "antd";
import {
    PlusOutlined, HomeOutlined, FilterOutlined, ClearOutlined, SearchOutlined,
    EyeOutlined, EditOutlined, DeleteOutlined, MoreOutlined, PlayCircleOutlined,
    CheckCircleOutlined, ProfileOutlined, FilePdfOutlined, FileExcelOutlined,
    ExperimentOutlined, TagsOutlined, BranchesOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../../services/axiosInstance";
import { isPermissionAllowed } from "../../../utils/permissionHelper";
import ReportPreviewModal from "../../../components/common/ReportPreviewModal";
import { buildReportPDF } from "../../../utils/reportPdfBuilder";
import { buildLabelPDF } from "../../../utils/labelPdfBuilder";
import { exportRowsToExcel, fmtMoney } from "../../../utils/reportExport";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { Option } = Select;

const PRODUCTION_STATUS_OPTIONS = [
    { value: "Pending", label: "Pending" },
    { value: "Ongoing", label: "Ongoing" },
    { value: "Finished", label: "Finished" },
];

const PRODUCTION_STATUS_COLORS = {
    "Pending": "orange",
    "Ongoing": "blue",
    "Finished": "green",
};

// Matches the server-side ceiling in BACKEND/api/production/list.php
const EXPORT_LIMIT = 2000;

const formatKg = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatQty = (value) => Number(value || 0).toLocaleString();

const productLabel = (record) => {
    if (!record) return "-";
    if (record.sp_code && record.sp_name) return `${record.sp_code} - ${record.sp_name}`;
    return record.sp_name || record.sp_code || "-";
};

const ManageProductionBatches = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [products, setProducts] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [viewVisible, setViewVisible] = useState(false);
    const [viewBatch, setViewBatch] = useState(null);
    const [editingBatch, setEditingBatch] = useState(null);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    // Advanced filter state
    const [search, setSearch] = useState("");
    const [filterProduct, setFilterProduct] = useState(undefined);
    const [filterStatus, setFilterStatus] = useState(undefined);
    const [applied, setApplied] = useState({});

    // PDF / Excel export state
    const [exporting, setExporting] = useState(false);
    const [printVisible, setPrintVisible] = useState(false);
    const [pdfDataUri, setPdfDataUri] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [includeFilters, setIncludeFilters] = useState(false);

    // Print Label state
    const [isLabelModalVisible, setIsLabelModalVisible] = useState(false);
    const [labelBatch, setLabelBatch] = useState(null);
    const [labelForm] = Form.useForm();
    const [labelPreviewVisible, setLabelPreviewVisible] = useState(false);
    const [labelPdfDataUri, setLabelPdfDataUri] = useState(null);
    const [labelPdfLoading, setLabelPdfLoading] = useState(false);
    const [labelPrinting, setLabelPrinting] = useState(false);
    const [labelMeta, setLabelMeta] = useState(null);

    // Assign Harvest Batch state
    const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
    const [assignBatch, setAssignBatch] = useState(null);
    const [harvestBatches, setHarvestBatches] = useState([]);
    const [assignedHarvest, setAssignedHarvest] = useState([]);
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignSaving, setAssignSaving] = useState(false);
    const [unassigningId, setUnassigningId] = useState(null);
    const [assignForm] = Form.useForm();

    const fetchRefData = useCallback(async () => {
        try {
            const productRes = await api.get("/masterdropdown/listof_activesalesproducts.php");
            setProducts(productRes.data.dataset || []);
        } catch {
            message.error("Failed to load sales products.");
        }
        try {
            const userRes = await api.get("/masterdropdown/listof_activeusers.php");
            setUsers(userRes.data.dataset || []);
        } catch {
            setUsers([]);
        }
        try {
            const harvestRes = await api.get("/masterdropdown/listof_harvestbatches.php");
            setHarvestBatches(harvestRes.data.dataset || []);
        } catch {
            setHarvestBatches([]);
        }
    }, []);

    const buildFilterPayload = (filters = {}) => ({
        search: filters.search || "",
        sp_id: filters.sp_id || 0,
        status: filters.status || "",
    });

    const fetchBatches = useCallback(async (page = 1, pageSize = 10, filters = {}) => {
        setLoading(true);
        try {
            const res = await api.post("/production/list.php", {
                page,
                limit: pageSize,
                ...buildFilterPayload(filters),
            });
            setBatches(res.data.dataset || []);
            setPagination({
                current: res.data.pagination?.current_page || 1,
                pageSize: res.data.pagination?.per_page || 10,
                total: res.data.pagination?.total_records || 0,
            });
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to fetch production batches.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRefData();
        fetchBatches(1, 10, {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilter = () => {
        const filters = {
            search: search.trim(),
            sp_id: filterProduct,
            status: filterStatus,
        };
        setApplied(filters);
        fetchBatches(1, pagination.pageSize, filters);
    };

    const handleClear = () => {
        setSearch("");
        setFilterProduct(undefined);
        setFilterStatus(undefined);
        setApplied({});
        fetchBatches(1, pagination.pageSize, {});
    };

    const openView = async (record) => {
        setViewBatch(record);
        setViewVisible(true);
        try {
            const res = await api.get(`/production/detail.php?id=${record.prodb_id}`);
            if (res.data.status === "success") setViewBatch(res.data);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to load production batch details.");
        }
    };

    const openEdit = (record) => {
        setEditingBatch(record);
        editForm.setFieldsValue({
            spId: record.sp_id ? Number(record.sp_id) : undefined,
            targetQty: record.target_qty,
            // Fall back to the stored figure only when the product has no unit weight.
            targetKg: computedTargetKg(record.sp_id, record.target_qty) ?? record.target_kg,
            sellingPrice: batchSellingPrice(record),
            prodStartDate: record.prod_start_date ? dayjs(record.prod_start_date) : undefined,
            prodEndDate: record.prod_end_date ? dayjs(record.prod_end_date) : undefined,
            expireDate: record.expire_date ? dayjs(record.expire_date) : undefined,
            actualQty: record.actual_qty || 0,
            actualKg: record.actual_kg || 0,
            processedBy: record.processed_by ? Number(record.processed_by) : undefined,
            qcApprovedQty: record.qc_approved_qty || 0,
            qcApprovedKg: record.qc_approved_kg || 0,
            qcRejectedQty: record.qc_rejecteded_qty || 0,
            qcRejectedKg: record.qc_rejecteded_kg || 0,
        });
        setIsEditModalVisible(true);
    };

    // Production outcome figures can only be recorded while the batch is running.
    const canEditBatch = (record) =>
        record && record.batch_status !== "Finished" &&
        ["Pending", "Ongoing"].includes(record.batch_status);

    // Unit weight (kg per unit) of the selected sales product - the basis for
    // the auto-calculated target weight.
    const unitWeightFor = (spId) => {
        const product = products.find((p) => Number(p.value) === Number(spId));
        return parseFloat(product?.sp_unit_kg || 0);
    };

    // Target weight is never typed by hand: qty x unit weight (sp_unit_kg).
    // The server recomputes it the same way, so the form shows exactly what
    // will be stored.
    const computedTargetKg = (spId, qty) => {
        const unitKg = unitWeightFor(spId);
        const quantity = parseFloat(qty || 0);
        return unitKg > 0 && quantity > 0
            ? Math.round(quantity * unitKg * 100) / 100
            : undefined;
    };

    const syncTargetWeight = (changed, allValues, targetForm) => {
        if (!("spId" in changed) && !("targetQty" in changed)) return;
        targetForm.setFieldsValue({ targetKg: computedTargetKg(allValues.spId, allValues.targetQty) });
        // Picking a product pre-fills its selling price. The field stays
        // editable - an override here applies to the whole batch and is logged.
        if ("spId" in changed) {
            const price = productSellingPriceFor(allValues.spId);
            if (price > 0) targetForm.setFieldsValue({ sellingPrice: price });
        }
    };

    // Selling price the batch stores: its own override, or the product's
    // current price for batches created before prices were held per batch.
    const batchSellingPrice = (record) => {
        const own = parseFloat(record?.selling_price || 0);
        if (own > 0) return own;
        return parseFloat(record?.sp_sellingprice || 0);
    };

    // Product default price, used to pre-fill the create / edit form.
    const productSellingPriceFor = (spId) => {
        const product = products.find((p) => Number(p.value) === Number(spId));
        return parseFloat(product?.sp_sellingprice || 0);
    };

    // Packed date on a label must sit inside the batch's production window.
    const packedDateBounds = (record) => {
        const start = record?.prod_start_date ? dayjs(record.prod_start_date).startOf("day") : null;
        let end = record?.prod_end_date ? dayjs(record.prod_end_date).endOf("day") : dayjs().endOf("day");
        if (start && end.isBefore(start, "day")) end = start;
        return { start, end };
    };

    const handleEdit = async (values) => {
        setLoading(true);
        try {
            const res = await api.post("/production/edit.php", {
                prodb_id: editingBatch.prodb_id,
                sp_id: values.spId,
                target_qty: values.targetQty,
                target_kg: values.targetKg,
                selling_price: values.sellingPrice || 0,
                prod_start_date: values.prodStartDate ? values.prodStartDate.format("YYYY-MM-DD") : "",
                prod_end_date: values.prodEndDate ? values.prodEndDate.format("YYYY-MM-DD") : "",
                expire_date: values.expireDate ? values.expireDate.format("YYYY-MM-DD") : "",
                actual_qty: values.actualQty || 0,
                actual_kg: values.actualKg || 0,
                processed_by: values.processedBy || 0,
                qc_approved_qty: values.qcApprovedQty || 0,
                qc_approved_kg: values.qcApprovedKg || 0,
                qc_rejecteded_qty: values.qcRejectedQty || 0,
                qc_rejecteded_kg: values.qcRejectedKg || 0,
            });
            if (res.data.status === "success") {
                message.success(res.data.message);
                setIsEditModalVisible(false);
                editForm.resetFields();
                setEditingBatch(null);
                handleFilter();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to update production batch.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: "Delete Production Batch",
            content: `Are you sure you want to delete production batch ${record.prodb_ref}? This action cannot be undone.`,
            okText: "Yes, Delete",
            okType: "danger",
            cancelText: "Cancel",
            maskClosable: false,
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.post("/production/delete.php", { prodb_id: record.prodb_id });
                    if (res.data.status === "success") {
                        message.success(res.data.message);
                        handleFilter();
                    } else throw new Error(res.data.message);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to delete production batch.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleStartProcessing = (record) => {
        Modal.confirm({
            title: "Start Production",
            content: `Start production batch ${record.prodb_ref}? The status will change to Ongoing.`,
            okText: "Yes, Start",
            okType: "primary",
            cancelText: "Cancel",
            maskClosable: false,
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.post("/production/start.php", { prodb_id: record.prodb_id });
                    if (res.data.status === "success") {
                        message.success(res.data.message);
                        handleFilter();
                    } else throw new Error(res.data.message);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to start production.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleFinishProcessing = (record) => {
        Modal.confirm({
            title: "Finish Production",
            content: `Finish production batch ${record.prodb_ref}? The status will change to Finished and the record becomes read only.`,
            okText: "Yes, Finish",
            okType: "primary",
            cancelText: "Cancel",
            maskClosable: false,
            onOk: async () => {
                setLoading(true);
                try {
                    const res = await api.post("/production/close.php", { prodb_id: record.prodb_id });
                    if (res.data.status === "success") {
                        message.success(res.data.message);
                        handleFilter();
                    } else throw new Error(res.data.message);
                } catch (err) {
                    message.error(err?.response?.data?.message || "Failed to finish production.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    /* ─── Print Label ────────────────────────────────────────── */

    const openLabel = (record) => {
        if (!record) return;
        setLabelBatch(record);
        const { start, end } = packedDateBounds(record);
        const today = dayjs().startOf("day");
        // Default to today when it is inside the production window, otherwise
        // fall back to the window start.
        let defaultDate = start && today.isBefore(start, "day") ? start : today;
        if (end && defaultDate.isAfter(end, "day")) defaultDate = start || end;
        labelForm.setFieldsValue({ packedDate: defaultDate, quantity: 10 });
        setIsLabelModalVisible(true);
    };

    const buildLabelDoc = (meta) => buildLabelPDF({
        quantity: meta.quantity,
        retailPrice: fmtMoney(meta.retailPrice),
        packedDate: meta.packedDate,
        expireDate: meta.expireDate,
        batchNo: meta.batchNo,
    });

    // Submitting the form only PREPARES the preview: nothing is written to the
    // database and no log entry is created until the user actually prints /
    // downloads the sheet from the preview.
    const handleLabelSubmit = (values) => {
        if (!labelBatch) return;
        const meta = {
            prodb_id: labelBatch.prodb_id,
            quantity: Number(values.quantity || 0),
            retailPrice: batchSellingPrice(labelBatch),
            packedDate: values.packedDate ? values.packedDate.format("YYYY-MM-DD") : "",
            expireDate: labelBatch.expire_date,
            batchNo: labelBatch.prodb_ref,
        };
        setLabelMeta(meta);
        setIsLabelModalVisible(false);
        labelForm.resetFields();

        // Build the sheet and show it in the shared PDF preview.
        setLabelPdfLoading(true);
        setLabelPdfDataUri(buildLabelDoc(meta).output("bloburl"));
        setLabelPreviewVisible(true);
        setLabelPdfLoading(false);
    };

    // The real print action: now that the user is printing the labels, record
    // the request in pb_labels and write the activity log, then download.
    const handleLabelDownload = async () => {
        if (!labelMeta || labelPrinting) return;
        setLabelPrinting(true);
        try {
            const res = await api.post("/production/label_create.php", {
                prodb_id: labelMeta.prodb_id,
                packed_date: labelMeta.packedDate,
                quantity: labelMeta.quantity,
            });
            if (res.data.status !== "success") throw new Error(res.data.message);

            buildLabelDoc(labelMeta).save(
                `production-labels-${labelMeta.batchNo}-${dayjs().format("YYYYMMDD-HHmm")}.pdf`
            );
            message.success(res.data.message || "Labels printed and logged.");
            handleFilter();
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || "Failed to print labels.");
        } finally {
            setLabelPrinting(false);
        }
    };

    /* ─── Assign Harvest Batch ───────────────────────────────── */

    const loadAssignedHarvest = useCallback(async (prodbId) => {
        if (!prodbId) { setAssignedHarvest([]); return; }
        setAssignLoading(true);
        try {
            const res = await api.post("/production/assigned_harvest.php", { prodb_id: prodbId });
            setAssignedHarvest(res.data.dataset || []);
        } catch (err) {
            setAssignedHarvest([]);
            message.error(err?.response?.data?.message || "Failed to load assigned harvest batches.");
        } finally {
            setAssignLoading(false);
        }
    }, []);

    const openAssign = (record) => {
        if (!record) return;
        setAssignBatch(record);
        assignForm.resetFields();
        setAssignedHarvest([]);
        setIsAssignModalVisible(true);
        loadAssignedHarvest(record.prodb_id);
    };

    const handleAssign = async (values) => {
        if (!assignBatch) return;
        const ids = (values.harvestbIds || []).map(Number).filter((n) => n > 0);
        if (ids.length === 0) {
            message.warning("Select at least one harvest batch to assign.");
            return;
        }
        setAssignSaving(true);
        try {
            const res = await api.post("/production/assign_harvest.php", {
                prodb_id: assignBatch.prodb_id,
                harvestb_ids: ids,
            });
            if (res.data.status !== "success") throw new Error(res.data.message);
            const skipped = res.data.skipped_count || 0;
            if (skipped > 0) {
                message.warning(`${res.data.message}. ${skipped} already assigned and skipped.`);
            } else {
                message.success(res.data.message);
            }
            assignForm.resetFields();
            loadAssignedHarvest(assignBatch.prodb_id);
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || "Failed to assign harvest batches.");
        } finally {
            setAssignSaving(false);
        }
    };

    const handleUnassign = async (record) => {
        if (!record || !assignBatch) return;
        setUnassigningId(record.harprodass_id);
        try {
            const res = await api.post("/production/unassign_harvest.php", { harprodass_id: record.harprodass_id });
            if (res.data.status !== "success") throw new Error(res.data.message);
            message.success(res.data.message);
            loadAssignedHarvest(assignBatch.prodb_id);
        } catch (err) {
            message.error(err?.response?.data?.message || err?.message || "Failed to remove the assignment.");
        } finally {
            setUnassigningId(null);
        }
    };

    const handleAdd = async (values) => {
        setLoading(true);
        try {
            const res = await api.post("/production/create.php", {
                sp_id: values.spId,
                target_qty: values.targetQty,
                target_kg: values.targetKg,
                selling_price: values.sellingPrice || 0,
                prod_start_date: values.prodStartDate ? values.prodStartDate.format("YYYY-MM-DD") : "",
                prod_end_date: values.prodEndDate ? values.prodEndDate.format("YYYY-MM-DD") : "",
                expire_date: values.expireDate ? values.expireDate.format("YYYY-MM-DD") : "",
                processed_by: values.processedBy || 0,
            });
            if (res.data.status === "success") {
                message.success(`${res.data.message} (${res.data.prodb_ref})`);
                setIsModalVisible(false);
                form.resetFields();
                handleFilter();
            } else throw new Error(res.data.message);
        } catch (err) {
            message.error(err?.response?.data?.message || "Failed to create production batch.");
        } finally {
            setLoading(false);
        }
    };

    /* ─── Export helpers ─────────────────────────────────────── */

    const buildExportPayload = () => ({
        page: 1,
        limit: EXPORT_LIMIT,
        ...buildFilterPayload(applied),
    });

    const fetchRowsForExport = async () => {
        const res = await api.post("/production/list.php", buildExportPayload());
        const rows = res.data.dataset || [];
        if (rows.length >= EXPORT_LIMIT) {
            message.warning(`Only the first ${EXPORT_LIMIT} matching batches were exported. Narrow the filters to export the rest.`);
        }
        return rows;
    };

    const EXPORT_COLUMNS = [
        { title: "Batch Ref", dataIndex: "prodb_ref", width: 16 },
        { title: "Product Code", dataIndex: "sp_code", width: 16 },
        { title: "Product Name", dataIndex: "sp_name", width: 26 },
        { title: "Start Date", dataIndex: "prod_start_date", width: 14 },
        { title: "End Date", dataIndex: "prod_end_date", width: 14 },
        { title: "Expire Date", dataIndex: "expire_date", width: 14 },
        { title: "Target Qty", dataIndex: "target_qty", width: 12 },
        { title: "Target Kg", dataIndex: "target_kg", width: 12 },
        { title: "Selling Price", dataIndex: "selling_price", width: 14 },
        { title: "Actual Qty", dataIndex: "actual_qty", width: 12 },
        { title: "Actual Kg", dataIndex: "actual_kg", width: 12 },
        { title: "QC Approved Qty", dataIndex: "qc_approved_qty", width: 15 },
        { title: "QC Approved Kg", dataIndex: "qc_approved_kg", width: 15 },
        { title: "QC Rejected Qty", dataIndex: "qc_rejecteded_qty", width: 15 },
        { title: "QC Rejected Kg", dataIndex: "qc_rejecteded_kg", width: 15 },
        { title: "Processed By", dataIndex: "processed_by_name", width: 22 },
        { title: "Status", dataIndex: "batch_status", width: 12 },
    ];

    const buildExportRows = (rows) => rows.map((r) => ({
        prodb_ref: r.prodb_ref || "-",
        sp_code: r.sp_code || "-",
        sp_name: r.sp_name || "-",
        prod_start_date: r.prod_start_date ? dayjs(r.prod_start_date).format("YYYY-MM-DD") : "-",
        prod_end_date: r.prod_end_date ? dayjs(r.prod_end_date).format("YYYY-MM-DD") : "-",
        expire_date: r.expire_date ? dayjs(r.expire_date).format("YYYY-MM-DD") : "-",
        target_qty: formatQty(r.target_qty),
        target_kg: fmtMoney(r.target_kg),
        selling_price: fmtMoney(batchSellingPrice(r)),
        actual_qty: formatQty(r.actual_qty),
        actual_kg: fmtMoney(r.actual_kg),
        qc_approved_qty: formatQty(r.qc_approved_qty),
        qc_approved_kg: fmtMoney(r.qc_approved_kg),
        qc_rejecteded_qty: formatQty(r.qc_rejecteded_qty),
        qc_rejecteded_kg: fmtMoney(r.qc_rejecteded_kg),
        processed_by_name: r.processed_by_name || "-",
        batch_status: r.batch_status || "-",
    }));

    const buildFiltersSummary = () => {
        const summary = {};
        if (applied.search) summary["Search"] = applied.search;
        if (applied.sp_id) {
            const found = products.find((p) => Number(p.value) === Number(applied.sp_id));
            summary["Product"] = found ? found.label : `#${applied.sp_id}`;
        }
        if (applied.status) summary["Status"] = applied.status;
        return summary;
    };

    const buildExportDoc = async (rows, withFilters) => buildReportPDF({
        title: "PRODUCTION BATCHES REPORT",
        subtitle: "Production batches",
        columns: EXPORT_COLUMNS.map((c) => ({ header: c.title })),
        rows: buildExportRows(rows).map((r) => EXPORT_COLUMNS.map((c) => r[c.dataIndex] ?? "")),
        includeFilters: withFilters,
        filters: buildFiltersSummary(),
        orientation: "landscape",
        columnStyles: {
            6: { halign: "right" }, 7: { halign: "right" },
            8: { halign: "right" }, 9: { halign: "right" },
            10: { halign: "right" }, 11: { halign: "right" },
            12: { halign: "right" }, 13: { halign: "right" },
        },
    });

    const handleExportPDF = async () => {
        setExporting(true);
        try {
            const rows = await fetchRowsForExport();
            if (rows.length === 0) {
                message.warning("No data to export.");
                return;
            }
            setPrintVisible(true);
            setPdfLoading(true);
            setPdfDataUri(null);
            const doc = await buildExportDoc(rows, includeFilters);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF export failed:", err);
            message.error(err?.response?.data?.message || "Failed to export PDF.");
        } finally {
            setPdfLoading(false);
            setExporting(false);
        }
    };

    const handleIncludeFiltersChange = async (checked) => {
        setIncludeFilters(checked);
        setPdfLoading(true);
        setPdfDataUri(null);
        try {
            const rows = await fetchRowsForExport();
            const doc = await buildExportDoc(rows, checked);
            setPdfDataUri(doc.output("bloburl"));
        } catch (err) {
            console.error("PDF generation failed:", err);
            message.error("Failed to generate PDF preview.");
        } finally {
            setPdfLoading(false);
        }
    };

    const handleDownloadPDF = async () => {
        try {
            const rows = await fetchRowsForExport();
            const doc = await buildExportDoc(rows, includeFilters);
            doc.save(`production-batches-${dayjs().format("YYYYMMDD-HHmm")}.pdf`);
            message.success("PDF downloaded successfully.");
        } catch (err) {
            console.error("PDF download failed:", err);
            message.error("Failed to download PDF.");
        }
    };

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const rows = await fetchRowsForExport();
            if (rows.length === 0) {
                message.warning("No data to export.");
                return;
            }
            exportRowsToExcel({
                filename: "production-batches",
                sheetName: "Production Batches",
                columns: EXPORT_COLUMNS,
                rows: buildExportRows(rows),
            });
            message.success("Excel exported successfully.");
        } catch (err) {
            console.error("Excel export failed:", err);
            message.error(err?.response?.data?.message || "Failed to export Excel.");
        } finally {
            setExporting(false);
        }
    };

    /* ─── Table ──────────────────────────────────────────────── */

    const columns = [
        {
            title: "Batch Ref",
            dataIndex: "prodb_ref",
            key: "prodb_ref",
            render: (v) => <Typography.Text strong>{v}</Typography.Text>,
        },
        {
            title: "Product",
            key: "product",
            render: (_, r) => productLabel(r),
        },
        {
            title: "Start Date",
            dataIndex: "prod_start_date",
            key: "prod_start_date",
            align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "End Date",
            dataIndex: "prod_end_date",
            key: "prod_end_date",
            align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "Expire Date",
            dataIndex: "expire_date",
            key: "expire_date",
            align: "center",
            render: (v) => (v ? dayjs(v).format("YYYY-MM-DD") : "-"),
        },
        {
            title: "Target (Qty/Kg)",
            key: "target",
            align: "right",
            render: (_, r) => (
                <span>{formatQty(r.target_qty)} / {formatKg(r.target_kg)} kg</span>
            ),
        },
        {
            title: "Status",
            dataIndex: "batch_status",
            key: "batch_status",
            align: "center",
            render: (s) => (
                <Tag color={PRODUCTION_STATUS_COLORS[s] || "default"} style={{ fontWeight: 600 }}>{s || "-"}</Tag>
            ),
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            fixed: "right",
            width: 90,
            render: (_, record) => {
                const status = record.batch_status;
                const canViewDetails = isPermissionAllowed('prod_batches_details');
                const canEdit = isPermissionAllowed('prod_batches_edit');
                const canDelete = isPermissionAllowed('prod_batches_delete');
                const canStart = isPermissionAllowed('prod_batches_start');
                const canFinish = isPermissionAllowed('prod_batches_finish');
                const canLabel = isPermissionAllowed('prod_batches_label');
                const canAssignHarvest = isPermissionAllowed('prod_batches_assign_harvest');
                const items = [
                    ...(canViewDetails ? [{
                        key: "summary", label: "View Summary", icon: <EyeOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openView(record); },
                    }] : []),
                    ...(canViewDetails ? [{
                        key: "full-details", label: "View Full Details", icon: <ProfileOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); navigate(`/production/production-batches/${record.prodb_id}`); },
                    }] : []),
                    ...(canLabel && status === "Ongoing" ? [{
                        key: "print-label", label: "Print Label", icon: <TagsOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openLabel(record); },
                    }] : []),
                    ...(canAssignHarvest && canEditBatch(record) ? [{
                        key: "assign-harvest", label: "Assign Harvest Batch", icon: <BranchesOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openAssign(record); },
                    }] : []),
                    ...(canStart && status === "Pending" ? [{
                        key: "start", label: "Start Production", icon: <PlayCircleOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleStartProcessing(record); },
                    }] : []),
                    ...(canFinish && status === "Ongoing" ? [{
                        key: "finish", label: "Finish Production", icon: <CheckCircleOutlined />,
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleFinishProcessing(record); },
                    }] : []),
                    ...(canEdit ? [{
                        key: "edit", label: "Edit", icon: <EditOutlined />, disabled: !canEditBatch(record),
                        onClick: (info) => { info?.domEvent?.stopPropagation(); openEdit(record); },
                    }] : []),
                    ...(canDelete ? [{
                        key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, disabled: status !== "Pending",
                        onClick: (info) => { info?.domEvent?.stopPropagation(); handleDelete(record); },
                    }] : []),
                ];
                if (items.length === 0) return null;
                return (
                    <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
                        <Button type="text" className="fluid-dots-btn" icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    /* ─── Modals ─────────────────────────────────────────────── */

    const renderPlanningFields = (currentForm, isEdit) => {
        // Once a batch is ongoing its product and production start date are
        // frozen, but the production end date, expire date and target figures
        // can still be corrected. Only an ongoing batch also accepts a hand
        // entered target weight - everywhere else the server derives it from
        // the product unit weight.
        const isOngoing = isEdit && editingBatch && editingBatch.batch_status === "Ongoing";
        const freezeProductAndStart = !!isOngoing;
        const targetWeightEditable = !!isOngoing;
        // A start date in the past can never be picked. On edit the batch's own
        // stored (possibly historical) date stays selectable, so an existing
        // plan is still savable without touching the date.
        const storedStart = isEdit && editingBatch?.prod_start_date
            ? dayjs(editingBatch.prod_start_date).startOf("day")
            : null;
        const isPastStart = (value) => !!value && value.isBefore(dayjs().startOf("day"));
        const disablePastStart = (current) => {
            if (!isPastStart(current)) return false;
            return storedStart ? !current.isSame(storedStart, "day") : true;
        };
        return (
            <>
                <Row gutter={[16, 0]}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Sales Product"
                            name="spId"
                            rules={[{ required: true, message: "Sales product is required" }]}
                        >
                            <Select
                                allowClear
                                disabled={freezeProductAndStart}
                                placeholder="Select Sales Product"
                                showSearch
                                optionFilterProp="children"
                            >
                                {products.map((p) => (
                                    <Option key={p.value} value={Number(p.value)}>{p.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Production Start Date"
                            name="prodStartDate"
                            rules={[
                                { required: true, message: "Production start date is required" },
                                {
                                    validator: (_, value) => {
                                        if (!isPastStart(value)) return Promise.resolve();
                                        if (storedStart && value.isSame(storedStart, "day")) return Promise.resolve();
                                        return Promise.reject(new Error("Start date cannot be earlier than today"));
                                    },
                                },
                            ]}
                        >
                            <DatePicker
                                style={{ width: "100%" }}
                                disabled={freezeProductAndStart}
                                disabledDate={(current) => disablePastStart(current)}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Production End Date"
                            name="prodEndDate"
                            rules={[{
                                validator: (_, value) => {
                                    const start = currentForm.getFieldValue("prodStartDate");
                                    if (!value || !start || !value.isBefore(start, "day")) return Promise.resolve();
                                    return Promise.reject(new Error("End date cannot be before the production start date"));
                                },
                            }]}
                        >
                            <DatePicker
                                style={{ width: "100%" }}
                                disabledDate={(current) => {
                                    const start = currentForm.getFieldValue("prodStartDate");
                                    return start ? current && current.isBefore(start, "day") : false;
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Expire Date"
                            name="expireDate"
                            rules={[
                                { required: true, message: "Expire date is required" },
                                {
                                    validator: (_, value) => {
                                        const start = currentForm.getFieldValue("prodStartDate");
                                        if (!value || !start || !value.isBefore(start, "day")) return Promise.resolve();
                                        return Promise.reject(new Error("Expire date cannot be before the production start date"));
                                    },
                                },
                            ]}
                        >
                            <DatePicker
                                style={{ width: "100%" }}
                                disabledDate={(current) => {
                                    const start = currentForm.getFieldValue("prodStartDate");
                                    return start ? current && current.isBefore(start, "day") : false;
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Target Quantity"
                            name="targetQty"
                            rules={[{ required: true, message: "Target quantity is required" }]}
                        >
                            <InputNumber style={{ width: "100%" }} min={1} precision={0} placeholder="e.g. 500" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Target Weight (Kg)"
                            name="targetKg"
                            rules={[{
                                required: true,
                                message: "Target weight is auto-calculated - select a product with a unit weight",
                            }]}
                        >
                            <InputNumber
                                style={{ width: "100%" }}
                                min={0.01}
                                step={0.01}
                                disabled={!targetWeightEditable}
                                placeholder={targetWeightEditable ? "Auto-calculated - editable" : "Auto-calculated"}
                            />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, cur) =>
                            prev.spId !== cur.spId || prev.targetQty !== cur.targetQty}>
                            {({ getFieldValue }) => {
                                const unitKg = unitWeightFor(getFieldValue("spId"));
                                const qty = parseFloat(getFieldValue("targetQty") || 0);
                                return (
                                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: -16, marginBottom: 12 }}>
                                        {unitKg > 0
                                            ? `Auto-calculated: ${qty} \u00d7 ${unitKg} kg per unit = ${formatKg(qty * unitKg)} kg${targetWeightEditable ? " - you can override it for an ongoing batch" : ""}`
                                            : "Select a sales product to auto-calculate the target weight."}
                                    </Typography.Text>
                                );
                            }}
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            label="Selling Price (Rs.)"
                            name="sellingPrice"
                            rules={[{ required: true, message: "Selling price is required" }]}
                        >
                            <InputNumber
                                style={{ width: "100%" }}
                                min={0}
                                step={0.01}
                                precision={2}
                                placeholder="Auto-filled from the product"
                            />
                        </Form.Item>
                        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.spId !== cur.spId}>
                            {({ getFieldValue }) => {
                                const productPrice = productSellingPriceFor(getFieldValue("spId"));
                                return (
                                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: -16, marginBottom: 12 }}>
                                        {productPrice > 0
                                            ? `Product default: Rs. ${fmtMoney(productPrice)}. Changing it sets this batch's retail price and is logged.`
                                            : "Select a sales product to pre-fill its selling price."}
                                    </Typography.Text>
                                );
                            }}
                        </Form.Item>
                    </Col>
                </Row>
            </>
        );
    };

    const renderOutcomeFields = (locked) => (
        <>
            <Divider orientation="left" style={{ marginTop: 0 }}>Production Outcome &amp; QC</Divider>
            {locked && (
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                    Actual and quality control figures are recorded from the Production Process page
                    (approvals and quality control update them), so they are read only here.
                </Typography.Text>
            )}
            <Row gutter={[16, 0]}>
                <Col xs={24} md={8}>
                    <Form.Item label="Actual Quantity" name="actualQty">
                        <InputNumber style={{ width: "100%" }} min={0} precision={0} placeholder="0" disabled={locked} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="Actual Weight (Kg)" name="actualKg">
                        <InputNumber style={{ width: "100%" }} min={0} step={0.01} placeholder="0.00" disabled={locked} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="Processed By" name="processedBy">
                        <Select
                            allowClear
                            placeholder="Select User"
                            showSearch
                            optionFilterProp="children"
                        >
                            {users.map((u) => (
                                <Option key={u.value} value={Number(u.value)}>{u.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="QC Approved Quantity" name="qcApprovedQty">
                        <InputNumber style={{ width: "100%" }} min={0} precision={0} placeholder="0" disabled={locked} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="QC Approved Weight (Kg)" name="qcApprovedKg">
                        <InputNumber style={{ width: "100%" }} min={0} step={0.01} placeholder="0.00" disabled={locked} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="QC Rejected Quantity" name="qcRejectedQty">
                        <InputNumber style={{ width: "100%" }} min={0} precision={0} placeholder="0" disabled={locked} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="QC Rejected Weight (Kg)" name="qcRejectedKg">
                        <InputNumber style={{ width: "100%" }} min={0} step={0.01} placeholder="0.00" disabled={locked} />
                    </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                    <Form.Item label="Status" style={{ marginBottom: 0 }}>
                        <Tag color={PRODUCTION_STATUS_COLORS[editingBatch?.batch_status] || "default"} style={{ fontWeight: 600 }}>
                            {editingBatch?.batch_status || "Pending"}
                        </Tag>

                    </Form.Item>
                </Col>
            </Row>
        </>
    );

    const renderCreateModal = () => (
        <Modal
            title="Create New Production Batch"
            open={isModalVisible}
            onCancel={() => { setIsModalVisible(false); form.resetFields(); }}
            footer={null}
            width={1040}
            style={{ top: 24 }}
            maskClosable={false}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAdd}
                onValuesChange={(changed, all) => syncTargetWeight(changed, all, form)}
            >
                <div style={{ maxHeight: "62vh", overflowY: "auto", overflowX: "hidden", paddingRight: 6 }}>
                    {renderPlanningFields(form, false)}
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Processed By" name="processedBy">
                                <Select
                                    allowClear
                                    placeholder="Select User"
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {users.map((u) => (
                                        <Option key={u.value} value={Number(u.value)}>{u.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={16}>
                            <Form.Item label="Status" style={{ marginBottom: 0 }}>
                                <Tag color={PRODUCTION_STATUS_COLORS["Pending"]} style={{ fontWeight: 600 }}>Pending</Tag>
                                <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                                    New batches start in Pending status and can be started from the batch list.
                                </Typography.Text>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
                <Row justify="end" style={{ marginTop: 16 }}>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button danger onClick={() => { setIsModalVisible(false); form.resetFields(); }}>Cancel</Button>
                            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>
                                Create Batch
                            </Button>
                        </Space>
                    </Form.Item>
                </Row>
            </Form>
        </Modal>
    );

    const renderEditModal = () => (
        <Modal
            title="Edit Production Batch"
            open={isEditModalVisible}
            onCancel={() => { setIsEditModalVisible(false); editForm.resetFields(); setEditingBatch(null); }}
            footer={null}
            width={1040}
            style={{ top: 24 }}
            maskClosable={false}
            destroyOnClose
        >
            <Form
                form={editForm}
                layout="vertical"
                onFinish={handleEdit}
                onValuesChange={(changed, all) => syncTargetWeight(changed, all, editForm)}
            >
                <div style={{ maxHeight: "62vh", overflowY: "auto", overflowX: "hidden", paddingRight: 6 }}>
                    {renderPlanningFields(editForm, true)}
                    {editingBatch && editingBatch.batch_status === "Ongoing"}
                    {renderOutcomeFields(editingBatch?.batch_status === "Ongoing")}
                </div>
                <Row justify="end" style={{ marginTop: 12 }}>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button danger onClick={() => { setIsEditModalVisible(false); editForm.resetFields(); setEditingBatch(null); }}>Cancel</Button>
                            <Button type="primary" htmlType="submit" icon={<EditOutlined />} loading={loading}>
                                Update Batch
                            </Button>
                        </Space>
                    </Form.Item>
                </Row>
            </Form>
        </Modal>
    );

    const renderViewModal = () => (
        <Modal
            title={viewBatch ? `Production Batch Summary - ${viewBatch.prodb_ref}` : "Production Batch Summary"}
            open={viewVisible}
            onCancel={() => setViewVisible(false)}
            footer={<Button type="primary" onClick={() => setViewVisible(false)}>Close</Button>}
            width={820}
            maskClosable={false}
            destroyOnClose
        >
            {viewBatch && (
                <>
                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                        Batch Information
                    </Typography.Title>
                    <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="Batch Ref" span={2}>
                            <Typography.Text strong>{viewBatch.prodb_ref}</Typography.Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Product" span={2}>{productLabel(viewBatch)}</Descriptions.Item>
                        <Descriptions.Item label="Status">
                            <Tag color={PRODUCTION_STATUS_COLORS[viewBatch.batch_status] || "default"} style={{ fontWeight: 600 }}>
                                {viewBatch.batch_status || "-"}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Processed By">{viewBatch.processed_by_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Production Start Date">
                            {viewBatch.prod_start_date ? dayjs(viewBatch.prod_start_date).format("YYYY-MM-DD") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Production End Date">
                            {viewBatch.prod_end_date ? dayjs(viewBatch.prod_end_date).format("YYYY-MM-DD") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Expire Date">
                            {viewBatch.expire_date ? dayjs(viewBatch.expire_date).format("YYYY-MM-DD") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Target Quantity">{formatQty(viewBatch.target_qty)}</Descriptions.Item>
                        <Descriptions.Item label="Target Weight (Kg)">{formatKg(viewBatch.target_kg)}</Descriptions.Item>
                        <Descriptions.Item label="Selling Price (Rs.)">
                            {fmtMoney(batchSellingPrice(viewBatch))}
                        </Descriptions.Item>
                        <Descriptions.Item label="Created By">{viewBatch.creator_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Created Date">
                            {viewBatch.cr_dt ? dayjs(viewBatch.cr_dt).format("YYYY-MM-DD HH:mm") : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Last Updated By">{viewBatch.updater_name || "-"}</Descriptions.Item>
                        <Descriptions.Item label="Last Updated Date">
                            {viewBatch.lc_dt ? dayjs(viewBatch.lc_dt).format("YYYY-MM-DD HH:mm") : "-"}
                        </Descriptions.Item>
                    </Descriptions>

                    <Divider style={{ margin: "16px 0" }} />

                    <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                        Production Outcome &amp; Quality Control
                    </Typography.Title>
                    <Descriptions bordered size="small" column={2}>
                        <Descriptions.Item label="Actual Quantity">{formatQty(viewBatch.actual_qty)}</Descriptions.Item>
                        <Descriptions.Item label="Actual Weight (Kg)">{formatKg(viewBatch.actual_kg)}</Descriptions.Item>
                        <Descriptions.Item label="QC Approved Quantity">{formatQty(viewBatch.qc_approved_qty)}</Descriptions.Item>
                        <Descriptions.Item label="QC Approved Weight (Kg)">{formatKg(viewBatch.qc_approved_kg)}</Descriptions.Item>
                        <Descriptions.Item label="QC Rejected Quantity">{formatQty(viewBatch.qc_rejecteded_qty)}</Descriptions.Item>
                        <Descriptions.Item label="QC Rejected Weight (Kg)">{formatKg(viewBatch.qc_rejecteded_kg)}</Descriptions.Item>
                    </Descriptions>
                </>
            )}
        </Modal>
    );

    const renderLabelModal = () => {
        const bounds = packedDateBounds(labelBatch);
        return (
            <Modal
                title={`Print Label${labelBatch?.prodb_ref ? ` - ${labelBatch.prodb_ref}` : ""}`}
                open={isLabelModalVisible}
                onCancel={() => { setIsLabelModalVisible(false); labelForm.resetFields(); }}
                footer={null}
                width={600}
                style={{ top: 24 }}
                maskClosable={false}
                destroyOnClose
            >
                <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Batch Ref">
                        <Typography.Text strong>{labelBatch?.prodb_ref || "-"}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Product">{productLabel(labelBatch)}</Descriptions.Item>
                    <Descriptions.Item label="Production Window">
                        {labelBatch?.prod_start_date ? dayjs(labelBatch.prod_start_date).format("YYYY-MM-DD") : "-"}
                        {"  to  "}
                        {labelBatch?.prod_end_date ? dayjs(labelBatch.prod_end_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Expire Date">
                        {labelBatch?.expire_date ? dayjs(labelBatch.expire_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                </Descriptions>
                <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
                    Review the preview first - the label request and activity log are recorded only
                    when you print / download the sheet.
                </Typography.Text>
                <Form form={labelForm} layout="vertical" onFinish={handleLabelSubmit}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Packed Date"
                                name="packedDate"
                                rules={[
                                    { required: true, message: "Packed date is required" },
                                    {
                                        validator: (_, value) => {
                                            if (!value) return Promise.resolve();
                                            if (bounds.start && value.isBefore(bounds.start, "day")) {
                                                return Promise.reject(new Error("Packed date cannot be before the production start date"));
                                            }
                                            if (bounds.end && value.isAfter(bounds.end, "day")) {
                                                return Promise.reject(new Error("Packed date cannot be after the production end date"));
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <DatePicker
                                    style={{ width: "100%" }}
                                    disabledDate={(current) => {
                                        if (!current) return false;
                                        if (bounds.start && current.isBefore(bounds.start, "day")) return true;
                                        if (bounds.end && current.isAfter(bounds.end, "day")) return true;
                                        return false;
                                    }}
                                />
                            </Form.Item>
                            <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: -16, marginBottom: 12 }}>
                                Only dates between the production start and end dates can be selected.
                            </Typography.Text>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Label Quantity"
                                name="quantity"
                                rules={[{ required: true, message: "Label quantity is required" }]}
                            >
                                <InputNumber style={{ width: "100%" }} min={1} max={100000} precision={0} placeholder="e.g. 50" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item label="Retail Price (Rs.) - read only">
                                <Input value={fmtMoney(batchSellingPrice(labelBatch))} prefix="Rs." disabled readOnly />
                            </Form.Item>
                            <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: -16, marginBottom: 12 }}>
                                This batch's selling price is printed on every label as the Retail Price.
                            </Typography.Text>
                        </Col>
                    </Row>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={() => { setIsLabelModalVisible(false); labelForm.resetFields(); }}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<TagsOutlined />}>
                                    Preview Labels
                                </Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>
        );
    };

    const renderAssignModal = () => {
        const assignedIds = new Set(assignedHarvest.map((h) => Number(h.harvestb_id)));
        const options = harvestBatches.filter((h) => !assignedIds.has(Number(h.value)));
        const assignedColumns = [
            {
                title: "Harvest Batch",
                dataIndex: "harvestb_ref",
                key: "harvestb_ref",
                render: (v) => <Typography.Text strong>{v || "-"}</Typography.Text>,
            },
            { title: "Season", dataIndex: "harvest_season", key: "harvest_season", render: (v) => v || "-" },
            { title: "Land", dataIndex: "land_name", key: "land_name", render: (v) => v || "-" },
            { title: "Assigned By", dataIndex: "added_by_name", key: "added_by_name", render: (v) => v || "-" },
            {
                title: "Assigned Date",
                dataIndex: "added_date",
                key: "added_date",
                render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "-"),
            },
            {
                title: "Action",
                key: "action",
                width: 110,
                align: "center",
                render: (_, record) => (
                    <Popconfirm
                        title="Remove assignment"
                        description={`Remove ${record.harvestb_ref || "this harvest batch"} from this production batch?`}
                        okText="Yes, Remove"
                        okType="danger"
                        cancelText="Cancel"
                        onConfirm={() => handleUnassign(record)}
                    >
                        <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            loading={unassigningId === record.harprodass_id}
                        >
                            Remove
                        </Button>
                    </Popconfirm>
                ),
            },
        ];
        const closeAssign = () => {
            setIsAssignModalVisible(false);
            assignForm.resetFields();
            setAssignedHarvest([]);
        };
        return (
            <Modal
                title={`Assign Harvest Batches${assignBatch?.prodb_ref ? ` - ${assignBatch.prodb_ref}` : ""}`}
                open={isAssignModalVisible}
                onCancel={closeAssign}
                footer={null}
                width={820}
                style={{ top: 24 }}
                maskClosable={false}
                destroyOnClose
            >
                <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="Batch Ref">
                        <Typography.Text strong>{assignBatch?.prodb_ref || "-"}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Product">{productLabel(assignBatch)}</Descriptions.Item>
                    <Descriptions.Item label="Status">
                        <Tag color={PRODUCTION_STATUS_COLORS[assignBatch?.batch_status] || "default"} style={{ fontWeight: 600 }}>
                            {assignBatch?.batch_status || "-"}
                        </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Production Window">
                        {assignBatch?.prod_start_date ? dayjs(assignBatch.prod_start_date).format("YYYY-MM-DD") : "-"}
                        {"  to  "}
                        {assignBatch?.prod_end_date ? dayjs(assignBatch.prod_end_date).format("YYYY-MM-DD") : "-"}
                    </Descriptions.Item>
                </Descriptions>

                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Select one or more harvest batches to link to this production batch."
                    description="A harvest batch already linked to this batch is not offered again, so the same harvest batch can never repeat in a single production batch."
                />

                <Form form={assignForm} layout="vertical" onFinish={handleAssign}>
                    <Form.Item
                        label="Harvest Batches"
                        name="harvestbIds"
                        rules={[{ required: true, message: "Select at least one harvest batch" }]}
                    >
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder={options.length > 0 ? "Search and select harvest batches" : "All harvest batches are already assigned"}
                            notFoundContent={assignLoading ? <Spin size="small" /> : "No harvest batches available"}
                            maxTagCount="responsive"
                        >
                            {options.map((h) => (
                                <Option key={h.value} value={Number(h.value)}>{h.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Row justify="end">
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Space>
                                <Button danger onClick={closeAssign}>Cancel</Button>
                                <Button type="primary" htmlType="submit" icon={<BranchesOutlined />} loading={assignSaving}>
                                    Assign Harvest Batches
                                </Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>

                <Divider orientation="left" style={{ marginTop: 20 }}>
                    Already Assigned ({assignedHarvest.length})
                </Divider>
                <Table
                    size="small"
                    rowKey="harprodass_id"
                    columns={assignedColumns}
                    dataSource={assignedHarvest}
                    loading={assignLoading}
                    pagination={false}
                    locale={{ emptyText: "No harvest batch assigned to this production batch yet" }}
                    scroll={{ x: 700, y: 200 }}
                />
            </Modal>
        );
    };

    const appliedProductLabel = applied.sp_id
        ? (products.find((p) => Number(p.value) === Number(applied.sp_id))?.label || "-")
        : "All Products";

    const appliedStatusLabel = applied.status || "All Statuses";

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between" gutter={[16, 12]}>
                    <Col>
                        <Breadcrumb
                            className="fluid-breadcrumb"
                            items={[
                                { href: '/dash', title: <HomeOutlined /> },
                                { title: 'Production' },
                                { title: 'Production Batches' },
                            ]}
                        />
                        <h1 className="fluid-page-title">
                            <ExperimentOutlined /> Production Batches
                        </h1>
                        <p className="fluid-page-subtitle">Plan production runs, record actual output and quality control results</p>
                    </Col>
                    <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                        <div className="hero-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
                            {isPermissionAllowed('prod_batches_create') && (
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => setIsModalVisible(true)}
                                    className="hero-action-btn"
                                >
                                    Create New Batch
                                </Button>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>

            <Content className="fluid-content">
                <div className="fluid-card">
                    {/* Advanced filter bar */}
                    <div className="fluid-filter-compact" style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onPressEnter={handleFilter}
                                    placeholder="Batch ref, product code or name"
                                    prefix={<SearchOutlined />}
                                    allowClear
                                />
                            </Col>
                            <Col xs={24} sm={12} lg={5}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Product:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterProduct}
                                    onChange={setFilterProduct}
                                    placeholder="All Products"
                                    allowClear
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {products.map((p) => <Option key={p.value} value={Number(p.value)}>{p.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={12} lg={3}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Status:</div>
                                <Select
                                    style={{ width: "100%" }}
                                    value={filterStatus}
                                    onChange={setFilterStatus}
                                    placeholder="All Statuses"
                                    allowClear
                                >
                                    {PRODUCTION_STATUS_OPTIONS.map((s) => <Option key={s.value} value={s.value}>{s.label}</Option>)}
                                </Select>
                            </Col>
                            <Col xs={24} sm={24} lg={4}>
                                <div className="fluid-filter-compact-actions">
                                    <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} className="fluid-btn-primary">
                                        Apply
                                    </Button>
                                    <Button icon={<ClearOutlined />} onClick={handleClear} className="fluid-btn-secondary">
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                            <Col xs={24} lg={7} style={{ textAlign: "right" }}>
                                {isPermissionAllowed('prod_batches_export') && (
                                    <Space wrap>
                                        <Tooltip title="Export to PDF (respects current filters)">
                                            <Button
                                                icon={<FilePdfOutlined style={{ color: "#ff4d4f" }} />}
                                                onClick={handleExportPDF}
                                                loading={exporting}
                                            >
                                                PDF
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Export to Excel (respects current filters)">
                                            <Button
                                                icon={<FileExcelOutlined style={{ color: "#52c41a" }} />}
                                                onClick={handleExportExcel}
                                                loading={exporting}
                                            >
                                                Excel
                                            </Button>
                                        </Tooltip>
                                    </Space>
                                )}
                            </Col>
                        </Row>
                        {(applied.search || applied.sp_id || applied.status) && (
                            <Typography.Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                                Showing results for — Search: {applied.search || "-"} · Product: {appliedProductLabel} · Status: {appliedStatusLabel}
                            </Typography.Text>
                        )}
                    </div>

                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={batches}
                            rowKey="prodb_id"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            onChange={(pg) => fetchBatches(pg.current, pg.pageSize, applied)}
                            loading={loading}
                            scroll={{ x: 1250 }}
                        />
                    </div>
                </div>
            </Content>

            <Footerdiv />

            {renderCreateModal()}
            {renderEditModal()}
            {renderViewModal()}
            {renderLabelModal()}
            {renderAssignModal()}

            <ReportPreviewModal
                visible={printVisible}
                onCancel={() => setPrintVisible(false)}
                title="Production Batches Report"
                pdfDataUri={pdfDataUri}
                loading={pdfLoading}
                includeFilters={includeFilters}
                onIncludeFiltersChange={handleIncludeFiltersChange}
                onDownload={handleDownloadPDF}
            />

            <ReportPreviewModal
                visible={labelPreviewVisible}
                onCancel={() => { setLabelPreviewVisible(false); setLabelPdfDataUri(null); }}
                title="Production Batch Labels - Preview"
                pdfDataUri={labelPdfDataUri}
                loading={labelPdfLoading}
                showFiltersOption={false}
                onDownload={handleLabelDownload}
            />
        </Layout>
    );
};

export default ManageProductionBatches;
